import {  Popover, Button, InputNumber, Space, Typography, Checkbox, Divider } from 'antd';
import { SettingOutlined } from '@ant-design/icons';

const { Text } = Typography;

/**
 * Popover that lets users set exact column widths and reset to defaults.
 *
 * Props:
 *   columns       — [{ key, label }] which columns to show
 *   widths        — { [key]: number } current widths
 *   onChange      — (key, width) => void
 *   defaults      — { [key]: number } default widths for "Reset"
 *   minWidth      — number (default 60)
 *   maxWidth      — number (default 600)
 */
const ColumnSettings = ({
  columns,
  widths,
  onChange,
  defaults,
  minWidth = 60,
  maxWidth = 600,
  resizableKeys,
  onToggleVisibility,
  onShowAll,
  hiddenCount = 0,
}) => {
  const canResize = (key) => !resizableKeys || resizableKeys.includes(key);
  const showVisibility = typeof onToggleVisibility === 'function';
  const visibleCount = columns.filter((col) => col.visible !== false).length;

  const handleReset = () => {
    columns
      .filter((col) => canResize(col.key) && defaults[col.key] != null)
      .forEach((col) => onChange(col.key, defaults[col.key]));
  };

  const groups = [];
    columns.forEach((col) => {
      const name = col.group ?? '';
      const last = groups[groups.length - 1];
      if (last && last.name === name) last.items.push(col);
      else groups.push({ name, items: [col] });
    });

    // A group holding a single column doesn't need a header — show the
    // group name as the row label instead (an unexpanded year, say).
    const sections = groups.map((group) =>
      group.name && group.items.length === 1
        ? { ...group, flat: true, items: [{ ...group.items[0], label: group.name }] }
        : { ...group, flat: false },
    );

 const content = (
    <div style={{ width: 260, maxHeight: 360, overflowY: 'auto' }}>
      <Space direction="vertical" size="small" style={{ width: '100%' }}>
        {sections.map((group) => {
  const shownInGroup = group.items.filter((c) => c.visible !== false).length;
  const allShown = shownInGroup === group.items.length;
  const wouldEmpty = allShown && shownInGroup === visibleCount;

  return (
    <div key={group.name || '_'} style={{ marginBottom: group.flat ? 0 : 6 }}>
      {group.name && !group.flat ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '2px 0' }}>
          {showVisibility ? (
            <Checkbox
              checked={allShown}
              indeterminate={shownInGroup > 0 && !allShown}
              disabled={wouldEmpty}
              onChange={(e) =>
                group.items.forEach((c) => onToggleVisibility(c.key, e.target.checked))
              }
            />
          ) : null}
          <Text strong style={{ fontSize: 12 }}>{group.name}</Text>
        </div>
      ) : null}

      {group.items.map((col) => {
        const isVisible = col.visible !== false;

        return (
          <div
            key={col.key}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              paddingLeft: showVisibility && !group.flat ? 22 : 0,
            }}
          >
            {showVisibility ? (
              <Checkbox
                checked={isVisible}
                disabled={isVisible && visibleCount === 1}
                onChange={(e) => onToggleVisibility(col.key, e.target.checked)}
              />
            ) : null}

            <Text
              style={{ flex: 1, minWidth: 0, opacity: isVisible ? 1 : 0.45 }}
              ellipsis
            >
              {col.label}
            </Text>

            {canResize(col.key) ? (
              <InputNumber
                size="small"
                min={minWidth}
                max={maxWidth}
                step={10}
                value={widths[col.key]}
                disabled={!isVisible}
                onChange={(value) => {
                  if (typeof value === 'number' && !Number.isNaN(value)) {
                    onChange(col.key, value);
                  }
                }}
                addonAfter="px"
                style={{ width: 110 }}
              />
            ) : null}
          </div>
        );
      })}
    </div>
  );
})}

        <Divider style={{ margin: '4px 0' }} />

        {onShowAll && hiddenCount > 0 ? (
          <Button size="small" onClick={onShowAll} style={{ width: '100%' }}>
            Show all ({hiddenCount} hidden)
          </Button>
        ) : null}

        <Button size="small" onClick={handleReset} style={{ width: '100%' }}>
          Reset widths
        </Button>
      </Space>
    </div>
  );

  return (
    <Popover
      content={content}
      title={showVisibility ? 'Columns' : 'Column widths'}
      trigger="click"
      placement="bottomRight"
    >
      <Button
        type="text"
        size="small"
        icon={<SettingOutlined style={{ fontSize: 12, color: '#999' }} />}
        onClick={(e) => e.stopPropagation()}
      />
    </Popover>
  );
};

export default ColumnSettings;